import { useEffect, useState } from "react";
import { Platform, View, Text, Pressable, StyleSheet, Animated, Linking } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { appColors } from "../constants/colors";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.rrkinstalments.instalmentcalculator";
const APP_STORE_URL = "https://apps.apple.com/us/app/instalment-calculator/id6752733221";

const STORAGE_KEY = "install-banner-dismissed-at";
const COOLDOWN_DAYS = 7;
const COOLDOWN_MS = COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

/**
 * Detects whether the web visitor is on Android or iOS and shows a
 * bottom banner directing them to the appropriate store listing.
 * Hidden on desktop browsers and when already running as a native app.
 */
export default function AppInstallBanner() {
  const [store, setStore] = useState(null); // "android" | "ios" | null
  const slideAnim = useState(new Animated.Value(120))[0];
  const nativeDriver = Platform.OS !== "web";

  useEffect(() => {
    if (Platform.OS !== "web") return;

    // Already running as installed PWA — don't show
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    // Respect the dismissal cooldown — don't show again until COOLDOWN_DAYS have passed
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - Number(dismissedAt);
      if (elapsed < COOLDOWN_MS) return;
    }

    const ua = navigator.userAgent;
    if (/android/i.test(ua)) {
      setStore("android");
    } else if (/iphone|ipad|ipod/i.test(ua)) {
      setStore("ios");
    }
    // Desktop visitors see nothing
  }, []);

  useEffect(() => {
    if (store) {
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: nativeDriver,
        tension: 60,
        friction: 10,
      }).start();
    }
  }, [store]);

  const dismiss = () => {
    Animated.timing(slideAnim, {
      toValue: 120,
      duration: 220,
      useNativeDriver: nativeDriver,
    }).start(() => {
      setStore(null);
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    });
  };

  const openStore = () => {
    const url = store === "android" ? PLAY_STORE_URL : APP_STORE_URL;
    Linking.openURL(url);
    dismiss();
  };

  if (!store) return null;

  const isAndroid = store === "android";

  return (
    <Animated.View style={[styles.banner, { transform: [{ translateY: slideAnim }] }]}>
      <View style={styles.iconBox}>
        <Ionicons
          name={isAndroid ? "logo-google-playstore" : "logo-apple-appstore"}
          size={26}
          color={appColors.primary}
        />
      </View>

      <View style={styles.textBox}>
        <Text style={styles.title}>Get the app</Text>
        <Text style={styles.subtitle}>
          {isAndroid ? "Download free on Google Play" : "Download free on the App Store"}
        </Text>
      </View>

      <View style={styles.actions}>
        <Pressable
          onPress={openStore}
          style={({ pressed }) => [styles.getBtn, pressed && styles.getBtnPressed]}
        >
          <Text style={styles.getBtnText}>{isAndroid ? "Play Store" : "App Store"}</Text>
        </Pressable>
        <Pressable
          onPress={dismiss}
          style={({ pressed }) => [styles.dismissBtn, pressed && styles.dismissBtnPressed]}
        >
          <Text style={styles.dismissText}>✕</Text>
        </Pressable>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  banner: {
    position: "fixed",
    bottom: 16,
    left: 16,
    right: 16,
    backgroundColor: appColors.cardBg,
    borderRadius: 16,
    flexDirection: "row",
    alignItems: "center",
    padding: 14,
    gap: 12,
    zIndex: 9999,
    borderWidth: 0.5,
    borderColor: appColors.border,
    boxShadow: "0 4px 24px rgba(5,30,60,0.18)",
  },

  iconBox: {
    width: 42,
    height: 42,
    borderRadius: 10,
    backgroundColor: appColors.primaryTint,
    alignItems: "center",
    justifyContent: "center",
  },

  textBox: {
    flex: 1,
    gap: 2,
  },

  title: {
    fontSize: 13,
    fontWeight: "600",
    color: appColors.textPrimary,
  },

  subtitle: {
    fontSize: 11,
    color: appColors.textSecondary,
    lineHeight: 15,
  },

  actions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },

  getBtn: {
    backgroundColor: appColors.primary,
    paddingVertical: 7,
    paddingHorizontal: 14,
    borderRadius: 10,
  },

  getBtnPressed: {
    opacity: 0.8,
  },

  getBtnText: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "500",
  },

  dismissBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: appColors.inputBg,
    alignItems: "center",
    justifyContent: "center",
  },

  dismissBtnPressed: {
    backgroundColor: appColors.borderLight,
  },

  dismissText: {
    fontSize: 11,
    color: appColors.textSecondary,
  },
});